import type {
  CycleCountVarianceReport,
  CycleCountVarianceReportItem,
  CycleCountVarianceSummary,
} from '../models/cycle-count-session.model';
import { parseAdjustedQty } from './cycle-count-adjustment-input.utils';
import { resolveDamagedQty } from './cycle-count-counting.utils';

/** A validated variance-adjustment payload: the new actual qty plus the resolved damage state. */
export interface VarianceAdjustmentInput {
  adjustedActualQty: number;
  damagedQty: number | null;
  isDamaged: boolean;
}

/**
 * Reason a variance-adjustment input was rejected. Callers map these to a message
 * (web toast / mobile alert) so the two platforms stay consistent (BUG-31302).
 */
export type VarianceAdjustmentInputError =
  | 'QTY_REQUIRED'
  | 'QTY_INVALID'
  | 'QTY_NEGATIVE'
  | 'BROKEN_INVALID'
  | 'BROKEN_NEGATIVE'
  // #31406: the web adjust modal requires an adjustment note; mobile still does not.
  | 'NOTE_REQUIRED';

/**
 * User-facing message for each variance-adjustment input error, shared so the web
 * toast and the mobile alert stay consistent (BUG-31302).
 */
export const VARIANCE_ADJUSTMENT_ERROR_MESSAGES: Record<
  VarianceAdjustmentInputError,
  string
> = {
  QTY_REQUIRED: 'Enter an adjusted count.',
  QTY_INVALID: 'Adjusted count must be a number.',
  QTY_NEGATIVE: 'Adjusted count cannot be negative.',
  BROKEN_INVALID: 'Broken quantity must be a number.',
  BROKEN_NEGATIVE: 'Broken quantity cannot be negative.',
  NOTE_REQUIRED: 'Enter a note explaining this adjustment.',
};

/** A validated variance-adjustment payload plus the required adjustment note (#31406). */
export interface VarianceAdjustmentWithNote extends VarianceAdjustmentInput {
  note: string;
}

/**
 * Parses the two raw fields of a post-session count adjustment — the adjusted
 * actual qty and the Broken/damaged qty — into a validated payload (BUG-31302).
 * Broken was editable during counting but had no path in variance review; this is
 * the shared gate used by web-warehouse's inline editor and the mobile adjust modal.
 *
 * The qty is required (an adjustment must set a count); Broken is optional, where
 * empty or 0 means no damage. Returns a `VarianceAdjustmentInputError` string when
 * either field is non-numeric or negative so the caller can warn instead of posting
 * garbage.
 */
export function parseVarianceAdjustmentInput(
  qtyText: string | null | undefined,
  brokenText: string | null | undefined
): VarianceAdjustmentInput | VarianceAdjustmentInputError {
  const qty = parseAdjustedQty(qtyText);
  if (qty === null) return 'QTY_REQUIRED';
  if (qty === 'INVALID') return 'QTY_INVALID';
  if (qty === 'NEGATIVE') return 'QTY_NEGATIVE';

  const damage = resolveDamagedQty(brokenText);
  if (damage === 'INVALID') return 'BROKEN_INVALID';
  if (damage === 'NEGATIVE') return 'BROKEN_NEGATIVE';

  return {
    adjustedActualQty: qty,
    damagedQty: damage.damagedQty,
    isDamaged: damage.isDamaged,
  };
}

/**
 * #31406: the web variance-review adjust modal's combined gate. Runs the shared
 * count + broken validation ({@link parseVarianceAdjustmentInput}) FIRST so a bad
 * count is reported ahead of the note, then requires a non-empty adjustment note.
 * Returns the validated payload with the trimmed note, or a
 * `VarianceAdjustmentInputError` (including `NOTE_REQUIRED`) so the caller can warn
 * instead of saving. Mobile keeps using {@link parseVarianceAdjustmentInput} and is
 * intentionally NOT subject to the note requirement.
 */
export function parseVarianceAdjustmentWithNote(
  qtyText: string | null | undefined,
  brokenText: string | null | undefined,
  noteText: string | null | undefined
): VarianceAdjustmentWithNote | VarianceAdjustmentInputError {
  const parsed = parseVarianceAdjustmentInput(qtyText, brokenText);
  if (typeof parsed === 'string') return parsed;

  const note = (noteText ?? '').trim();
  if (note === '') return 'NOTE_REQUIRED';

  return { ...parsed, note };
}

/**
 * True when a variance-report row may be adjusted during review (BUG-31302).
 * Any counted row qualifies — including a "match" row (qty equals expected, zero
 * variance) that nonetheless has broken/damaged units — plus not-found rows, so a
 * found item can still be corrected. An uncounted, non-not-found row has nothing to
 * adjust. This widened the old "variance rows only" gate so Broken is editable on a
 * count-matches-but-damaged item, which otherwise had no edit path.
 */
export function canAdjustVarianceReportRow(
  item: Pick<CycleCountVarianceReportItem, 'actualQty' | 'isNotFound'>
): boolean {
  return item.actualQty != null || item.isNotFound;
}

/**
 * Returns a new report item with the post-session count adjustment applied.
 * The original ActualQty is preserved for audit; variance and cost are recomputed.
 */
export function applyCountAdjustmentToReportItem(
  item: CycleCountVarianceReportItem,
  adjustedActualQty: number,
  adjustedBy: string
): CycleCountVarianceReportItem {
  const newVariance = adjustedActualQty - item.expectedQty;
  const newIsVariance = newVariance !== 0;
  const newExtendedCost =
    item.unitCost != null ? newVariance * item.unitCost : null;

  return {
    ...item,
    isNotFound: false,
    variance: newVariance,
    extendedVarianceCost: newExtendedCost,
    isVariance: newIsVariance,
    isAdjusted: true,
    originalActualQty: item.actualQty,
    adjustedActualQty,
    adjustedBy,
    adjustedDate: new Date().toISOString(),
  };
}

/**
 * Returns a new array with the adjustment applied to the matching item (by materialId + subLocationCode).
 * All other items are returned unchanged. Original array is not mutated.
 */
export function applyCountAdjustmentToReportItems(
  items: CycleCountVarianceReportItem[],
  materialId: string,
  subLocationCode: string,
  adjustedActualQty: number,
  adjustedBy: string
): CycleCountVarianceReportItem[] {
  return items.map((item) =>
    item.materialId === materialId && item.subLocationCode === subLocationCode
      ? applyCountAdjustmentToReportItem(item, adjustedActualQty, adjustedBy)
      : item
  );
}

/**
 * Returns a new summary with varianceCount, matchedCount, and totalDollarImpact
 * recalculated after one item has been adjusted. Original summary is not mutated.
 */
export function recalculateSummaryAfterCountAdjustment(
  summary: CycleCountVarianceSummary,
  oldItem: CycleCountVarianceReportItem,
  newItem: CycleCountVarianceReportItem
): CycleCountVarianceSummary {
  const oldCost = oldItem.extendedVarianceCost ?? 0;
  const newCost = newItem.extendedVarianceCost ?? 0;

  const varianceCountDelta =
    (newItem.isVariance ? 1 : 0) - (oldItem.isVariance ? 1 : 0);
  const matchedCountDelta = -varianceCountDelta;

  return {
    ...summary,
    varianceCount: summary.varianceCount + varianceCountDelta,
    matchedCount: summary.matchedCount + matchedCountDelta,
    totalDollarImpact: summary.totalDollarImpact - oldCost + newCost,
  };
}

/**
 * Returns a new report with one item replaced by its adjusted version and the summary
 * recalculated (BUG-31302 count adjustment). The original report is not mutated; if the target
 * item id is not present the report is returned unchanged.
 */
export function applyCountAdjustmentToReport(
  report: CycleCountVarianceReport,
  targetItemId: string,
  updatedItem: CycleCountVarianceReportItem
): CycleCountVarianceReport {
  const oldItem = report.items.find((i) => i.cycleCountItemId === targetItemId);
  if (!oldItem) {
    return report;
  }
  const items = report.items.map((i) =>
    i.cycleCountItemId === targetItemId ? updatedItem : i
  );
  const summary = recalculateSummaryAfterCountAdjustment(
    report.summary,
    oldItem,
    updatedItem
  );
  return { summary, items };
}
